import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useApiPost, useApiDelete } from '../../../../api/hooks';
import LabeledInput from '../controls/LabeledInput';
import LabeledSelect from '../controls/LabeledSelect';
import MembersGrid from './MembersGrid';
import StudentModal from './StudentModal';

const TEAMS_URL = '/api/camps/admin/teams';
const DEFAULT_COLOR = '#243b82';

// Панель команды: форма (название/цвет/организатор) + участники + модалка игрока.
export default function TeamPanel({ team, teams, orgList, onSave, onDelete }) {
    const [form, setForm] = useState({ name: '', color: DEFAULT_COLOR, orgId: '' });
    const [selectedStudent, setSelectedStudent] = useState(null);

    useEffect(() => {
        setForm({
            name: team.name || '',
            color: team.color || DEFAULT_COLOR,
            orgId: team.org?.id ?? ''
        });
    }, [team.id, team.name, team.color, team.org]);

    const saveTeam = useApiPost({ successMessage: 'Команда сохранена', withPreloader: true, onSuccess: onSave });
    const deleteTeam = useApiDelete({ successMessage: 'Команда удалена', withPreloader: true, onSuccess: onDelete });

    const setColor = (value) => setForm(prev => ({ ...prev, color: value }));

    const handleSave = () => {
        const selectedOrg = orgList.find(org => org.id === form.orgId) || null;
        saveTeam.mutate({
            url: TEAMS_URL,
            data: { ...team, name: form.name, color: form.color, org: selectedOrg }
        });
    };

    const handleDelete = () => {
        deleteTeam.mutate({ url: `${TEAMS_URL}/${team.id}` });
    };

    const orgOptions = orgList.map(org => ({
        key: org.id,
        value: `${org.name || ''} ${org.surname || ''}`.trim() || `Организатор ${org.id}`
    }));

    const students = team.studentSet || [];

    return (
        <>
            <LabeledInput
                className="set-field"
                label="Название команды"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            />

            <div className="set-field">
                <label className="ctl-label">Цвет команды</label>
                <div className="tm-color">
                    <input
                        className="input mono"
                        value={form.color.toUpperCase()}
                        onChange={(e) => setColor(e.target.value)}
                    />
                    <input
                        type="color"
                        className="tm-swatch"
                        value={form.color}
                        onChange={(e) => setColor(e.target.value)}
                        aria-label="Выбрать цвет"
                    />
                </div>
            </div>

            <LabeledSelect
                className="set-field"
                label="Организатор"
                placeholder="Не назначен"
                options={orgOptions}
                value={form.orgId}
                onChange={(e) => setForm(prev => ({ ...prev, orgId: e.target.value ? Number(e.target.value) : '' }))}
            />

            <div className="tm-acts">
                <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleteTeam.isPending}>
                    Удалить
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saveTeam.isPending}>
                    Сохранить
                </button>
            </div>

            <div className="tm-divider" />

            <MembersGrid students={students} color={form.color} onSelect={setSelectedStudent} />

            <StudentModal
                open={Boolean(selectedStudent)}
                onClose={() => setSelectedStudent(null)}
                student={selectedStudent}
                teamColor={form.color}
                teams={teams}
                onSaved={onSave}
            />
        </>
    );
}

TeamPanel.propTypes = {
    team: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        color: PropTypes.string,
        org: PropTypes.shape({ id: PropTypes.number }),
        studentSet: PropTypes.array
    }).isRequired,
    teams: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string
    })),
    orgList: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        surname: PropTypes.string
    })).isRequired,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

TeamPanel.defaultProps = { teams: [] };
